import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Button, Chip, Tabs, Tab, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, InputAdornment, Snackbar, Alert,
  TablePagination, CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  EditOutlined as EditIcon,
  Search as SearchIcon,
  ListAlt as TypesIcon,
  Description as PolicyIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import { fetchLeaveTypes } from "../../actions/Leave/LeaveTypeAction";
import { fetchLeavePolicies, saveLeaveType, saveLeavePolicy } from "../../actions/Leave/LeaveConfigAction";


const NAVY = "#142c54";
const cardSx = {
  borderRadius: "12px",
  border: "1px solid #eef0f4",
  boxShadow: "0px 2px 8px rgba(20, 44, 84, 0.06)",
  backgroundColor: "#fff",
};

//Helpers

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (k) => new Date(`${k}T00:00:00`);
const fmtDate = (k) =>
  k ? fromKey(k).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const EMPTY_TYPE = { id: null, code: "", name: "", paid: true, active: true };
// Every policy is saved for Executive (employee type 1); the user is not asked to pick one
const DEFAULT_EMPLOYEE_TYPE_ID = "1";
const EMPTY_POLICY = { id: null, employeeTypeId: DEFAULT_EMPLOYEE_TYPE_ID, leaveTypeId: "", days: "", from: "", to: "", active: true };

const StatusChip = ({ active }) => (
  <Chip
    label={active ? "Active" : "Inactive"}
    size="small"
    sx={{
      fontWeight: 700, fontSize: 11,
      color: active ? "#15803d" : "#64748b",
      backgroundColor: active ? "#dcfce7" : "#f1f5f9",
    }}
  />
);

const headCellSx = { fontWeight: 700, color: "#fff", backgroundColor: "#154c79", fontSize: 12 };

//Page

const LeaveConfiguration = () => {
  const [tab, setTab] = useState(0);
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.auth?.serviceNo);
  const { data: rawTypes = [], loading: typesLoading, error: typesError } = useSelector((s) => s.leaveTypes) || {};
  const { data: policies = [], loading: policiesLoading, error: policiesError } = useSelector((s) => s.leavePolicies) || {};
  const leaveTypes = useMemo(() => rawTypes.map((t) => ({ ...t, paid: t.isPaid, active: t.isActive })), [rawTypes]);
  const refreshing = typesLoading || policiesLoading;
  const [saving, setSaving] = useState(false);

  // filters
  const [typeSearch, setTypeSearch] = useState("");
  const [typeStatus, setTypeStatus] = useState("active");
  const [policySearch, setPolicySearch] = useState("");
  const [policyStatus, setPolicyStatus] = useState("active");

  // pagination
  const [typePage, setTypePage] = useState(0);
  const [policyPage, setPolicyPage] = useState(0);
  const rowsPerPage = 8;

  // dialogs
  const [typeDialog, setTypeDialog] = useState(false);
  const [typeForm, setTypeForm] = useState(EMPTY_TYPE);
  const [typeErrors, setTypeErrors] = useState({});
  const [policyDialog, setPolicyDialog] = useState(false);
  const [policyForm, setPolicyForm] = useState(EMPTY_POLICY);
  const [policyErrors, setPolicyErrors] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const loadAll = useCallback(() => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeavePolicies());
  }, [dispatch]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const e = typesError || policiesError;
    if (e) notify(e, "error");
  }, [typesError, policiesError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh reloads the data and also clears any applied filters (back to the defaults)
  const handleRefresh = () => {
    setTypeSearch("");
    setTypeStatus("active");
    setTypePage(0);
    setPolicySearch("");
    setPolicyStatus("active");
    setPolicyPage(0);
    loadAll();
  };

  const typeMap = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t])), [leaveTypes]);

//   Leave Types
  const filteredTypes = useMemo(() => {
    const q = typeSearch.trim().toLowerCase();
    return leaveTypes.filter((t) =>{
      const matchQ = !q || t.code.toLowerCase().includes(q)||t.name.toLowerCase().includes(q);
      const matchS = typeStatus === "all" || (typeStatus === "active" ? t.active : !t.active);
      return matchQ && matchS;
    })
    .sort((a,b) => Number(!!b.active) - Number(!!a.active));
  }, [leaveTypes, typeSearch, typeStatus]);

  const openAddType = () => {
    setTypeForm(EMPTY_TYPE);
    setTypeErrors({});
    setTypeDialog(true);
  };
  const openEditType = (t) => {
    setTypeForm({ ...t });
    setTypeErrors({});
    setTypeDialog(true);
  };
  const setTypeField = (k, v) => {
    setTypeForm((f) => ({ ...f, [k]: v }));
    setTypeErrors((e) => ({ ...e, [k]: undefined }));
  };

  const saveType = async () => {
    const errs = {};
    const code = typeForm.code.trim().toUpperCase();
    const name = typeForm.name.trim();
    if (!code) errs.code = "Code is required";
    else if (leaveTypes.some((t) => t.code === code && t.id !== typeForm.id)) errs.code = "This code already exists";
    if (!name) errs.name = "Leave name is required";
    else if (leaveTypes.some((t) => t.name.toLowerCase() === name.toLowerCase() && t.id !== typeForm.id))
      errs.name = "This leave name already exists";
    if (Object.keys(errs).length) {
      setTypeErrors(errs);
      return;
    }
    if (!userId) return notify("Your user session was not found. Please sign in again.", "error");

    // new types go to the end of the list; an edited type keeps its own order
    const maxOrder = leaveTypes.reduce((m, t) => Math.max(m, Number(t.displayOrder) || 0), 0);
    setSaving(true);
    const res = await dispatch(
      saveLeaveType({
        id: typeForm.id, code, name,
        paid: !!typeForm.paid, active: !!typeForm.active,
        displayOrder: typeForm.id ? Number(typeForm.displayOrder) || maxOrder + 1 : maxOrder + 1,
        userId,
      })
    );
    setSaving(false);
    if (!res.ok) return notify(res.message, "error");
    notify(typeForm.id ? "Leave type updated" : "Leave type added");
    setTypeDialog(false);
  };

//  Leave Policies
  const filteredPolicies = useMemo(() => {
    const q = policySearch.trim().toLowerCase();
    return policies.filter((p) => {
      const lt = typeMap[p.leaveTypeId]?.name || "";
      const matchQ = !q || lt.toLowerCase().includes(q);
      const matchS = policyStatus === "all" || (policyStatus === "active" ? p.active : !p.active);
      return matchQ && matchS;
    })
    .sort((a,b) => Number(!!b.active) - Number(!!a.active));
  }, [policies, policySearch, policyStatus, typeMap]);

  const openAddPolicy = () => {
    setPolicyForm(EMPTY_POLICY);
    setPolicyErrors({});
    setPolicyDialog(true);
  };
  const openEditPolicy = (p) => {
    setPolicyForm({ ...p, days: p.days > 0 ? String(p.days) : "" });
    setPolicyErrors({});
    setPolicyDialog(true);
  };
  const setPolicyField = (k, v) => {
    setPolicyForm((f) => ({ ...f, [k]: v }));
    setPolicyErrors((e) => ({ ...e, [k]: undefined }));
  };

  const savePolicy = async () => {
    const errs = {};
    const f = policyForm;
    if (!f.leaveTypeId) errs.leaveTypeId = "Leave type is required";
    // Days per year is required
    const hasDays = String(f.days).trim() !== "";
    const days = hasDays ? Number(f.days) : 0;
    if (!hasDays) errs.days = "Days per year is required";
    else if (isNaN(days)) errs.days = "Enter a valid number of days";
    else if (days <= 0) errs.days = "Must be greater than 0";
    else if (days > 366) errs.days = "Cannot exceed 366";
    // Effective dates are optional; only check the order when both are given
    if (f.from && f.to && f.to < f.from) errs.to = "End date must be after the start date";

    if (!Object.keys(errs).length && f.active) {
      const clash = policies.some(
        (p) =>
          p.id !== f.id && p.active &&
          p.leaveTypeId === f.leaveTypeId &&
          (p.from || "0000-00-00") <= (f.to || "9999-12-31") &&
          (p.to || "9999-12-31") >= (f.from || "0000-00-00")
      );
      if (clash) errs.leaveTypeId = "An active policy already covers this leave type in this period";
    }
    if (Object.keys(errs).length) {
      setPolicyErrors(errs);
      return;
    }
    if (!userId) return notify("Your user session was not found. Please sign in again.", "error");

    setSaving(true);
    const res = await dispatch(saveLeavePolicy({ ...f, employeeTypeId: DEFAULT_EMPLOYEE_TYPE_ID, days, userId }));
    setSaving(false);
    if (!res.ok) return notify(res.message, "error");
    notify(f.id ? "Leave policy updated" : "Leave policy added");
    setPolicyDialog(false);
  };

  const onTab = (e, v) => setTab(v);
  const activeTypes = leaveTypes.filter((t) => t.active);

//   Render
  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <HeaderBannerPage />
      <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4, px: 2, pb: 4 }}>
        {/* Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#fff", display: "flex", justifyContent: "space-between",
            alignItems: "center", flexWrap: "wrap", gap: 2,
            boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
          }}
        >
          <Box>
            <Chip
              label="LEAVE MANAGEMENT"
              size="small"
              sx={{ fontWeight: "bold", mb: 1, fontSize: "11px", letterSpacing: "0.5px", backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
              Leave Configuration
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
              Manage leave types and yearly entitlements
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              startIcon={<RefreshIcon sx={refreshing ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
              sx={{
                height: 38, px: 2.5, borderRadius: "8px", fontWeight: "bold", textTransform: "none",
                color: "#fff", border: "1px solid rgba(255,255,255,0.5)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.6)" },
              }}
            >
              Refresh
            </Button>
            <Button
              onClick={tab === 0 ? openAddType : openAddPolicy}
              startIcon={<AddIcon />}
              sx={{
                height: 38, px: 2.5, borderRadius: "8px", fontWeight: "bold", textTransform: "none",
                backgroundColor: "#fff", color: NAVY, "&:hover": { backgroundColor: "#e8eefc" },
              }}
            >
              {tab === 0 ? "Add Leave Type" : "Add Policy"}
            </Button>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Tabs
            value={tab}
            onChange={onTab}
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: NAVY },
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
              "& .MuiTab-root.Mui-selected": { color: NAVY },
            }}
          >
            <Tab icon={<TypesIcon fontSize="small" />} iconPosition="start" label="Leave Types" />
            <Tab icon={<PolicyIcon fontSize="small" />} iconPosition="start" label="Leave Policies" />
          </Tabs>
        </Paper>

        {/* Leave Types */}
        {tab === 0 && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by code or name..."
                value={typeSearch}
                onChange={(e) => { setTypeSearch(e.target.value); setTypePage(0); }}
                sx={{ flex: 1, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={typeStatus} label="Status"
                  onChange={(e) => { setTypeStatus(e.target.value); setTypePage(0); }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer sx={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Code</TableCell>
                    <TableCell sx={headCellSx}>Leave Name</TableCell>
                    <TableCell sx={headCellSx} align="center">Paid</TableCell>
                    <TableCell sx={headCellSx} align="center">Status</TableCell>
                    <TableCell sx={headCellSx} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        No leave types found.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredTypes.slice(typePage * rowsPerPage, typePage * rowsPerPage + rowsPerPage).map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{t.code}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{t.name}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={t.paid ? "Paid" : "No Pay"}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: 11,
                            color: t.paid ? "#047857" : "#b45309",
                            backgroundColor: t.paid ? "#d1fae5" : "#fef3c7",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center"><StatusChip active={t.active} /></TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openEditType(t)} sx={{ color: "#3b82f6" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTypes.length}
              page={typePage}
              onPageChange={(e, p) => setTypePage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </Paper>
        )}

        {/* Leave Policies */}
        {tab === 1 && (
          <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by leave type..."
                value={policySearch}
                onChange={(e) => { setPolicySearch(e.target.value); setPolicyPage(0); }}
                sx={{ flex: 1, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={policyStatus} label="Status"
                  onChange={(e) => { setPolicyStatus(e.target.value); setPolicyPage(0); }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer sx={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Leave Type</TableCell>
                    <TableCell sx={headCellSx} align="center">Days / Year</TableCell>
                    <TableCell sx={headCellSx}>Effective From</TableCell>
                    <TableCell sx={headCellSx}>Effective To</TableCell>
                    <TableCell sx={headCellSx} align="center">Status</TableCell>
                    <TableCell sx={headCellSx} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPolicies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        No leave policies found.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredPolicies.slice(policyPage * rowsPerPage, policyPage * rowsPerPage + rowsPerPage).map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{typeMap[p.leaveTypeId]?.name || "—"}</TableCell>
                      <TableCell align="center" sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{p.days > 0 ? p.days : <span style={{ fontWeight: 500, color: "#64748b", fontSize: 12 }}>No fixed quota</span>}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{fmtDate(p.from)}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{fmtDate(p.to)}</TableCell>
                      <TableCell align="center"><StatusChip active={p.active} /></TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openEditPolicy(p)} sx={{ color: "#3b82f6" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredPolicies.length}
              page={policyPage}
              onPageChange={(e, p) => setPolicyPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </Paper>
        )}
      </Box>

      {/* Leave Type Dialog */}
      <Dialog open={typeDialog} onClose={() => setTypeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {typeForm.id ? "Edit Leave Type" : "Add Leave Type"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Code" size="small" fullWidth required placeholder="e.g. ANL"
            value={typeForm.code}
            onChange={(e) => setTypeField("code", e.target.value.toUpperCase())}
            inputProps={{ maxLength: 5 }}
            error={!!typeErrors.code} helperText={typeErrors.code}
          />
          <TextField
            label="Leave Name" size="small" fullWidth required placeholder="e.g. Annual Leave"
            value={typeForm.name}
            onChange={(e) => setTypeField("name", e.target.value)}
            error={!!typeErrors.name} helperText={typeErrors.name}
          />
          <FormControlLabel
            control={<Switch checked={typeForm.paid} onChange={(e) => setTypeField("paid", e.target.checked)} color="success" />}
            label={typeForm.paid ? "Paid leave" : "No pay leave"}
          />
          <FormControlLabel
            control={<Switch checked={typeForm.active} onChange={(e) => setTypeField("active", e.target.checked)} color="success" />}
            label={typeForm.active ? "Active" : "Inactive"}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTypeDialog(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={saveType} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#0e3c66" } }}
          >
            {typeForm.id ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* leave policy dialog */}
      <Dialog open={policyDialog} onClose={() => setPolicyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {policyForm.id ? "Edit Leave Policy" : "Add Leave Policy"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>

          <FormControl size="small" fullWidth required error={!!policyErrors.leaveTypeId}>
            <InputLabel>Leave Type</InputLabel>
            <Select
              value={policyForm.leaveTypeId} label="Leave Type"
              onChange={(e) => setPolicyField("leaveTypeId", e.target.value)}
            >
              {activeTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
            {policyErrors.leaveTypeId && (
              <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5, ml: 1.75 }}>{policyErrors.leaveTypeId}</Typography>
            )}
          </FormControl>

          <TextField
            label="Days per Year" size="small" fullWidth required type="number"
            inputProps={{ min: 0, step: 0.5 }}
            value={policyForm.days}
            onChange={(e) => setPolicyField("days", e.target.value)}
            error={!!policyErrors.days} helperText={policyErrors.days}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Effective From"
              value={policyForm.from ? fromKey(policyForm.from) : null}
              onChange={(d) => setPolicyField("from", d && !isNaN(d.getTime()) ? toKey(d) : "")}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small", fullWidth: true,
                  error: !!policyErrors.from, helperText: policyErrors.from,                },
              }}
            />
            <DatePicker
              label="Effective To"
              value={policyForm.to ? fromKey(policyForm.to) : null}
              onChange={(d) => setPolicyField("to", d && !isNaN(d.getTime()) ? toKey(d) : "")}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small", fullWidth: true,
                  error: !!policyErrors.to, helperText: policyErrors.to,
                },
              }}
            />
          </LocalizationProvider>

          <FormControlLabel
            control={<Switch checked={policyForm.active} onChange={(e) => setPolicyField("active", e.target.checked)} color="success" />}
            label={policyForm.active ? "Active" : "Inactive"}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPolicyDialog(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={savePolicy} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#0e3c66" } }}
          >
            {policyForm.id ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: "8px", fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveConfiguration;